import dayjs from 'dayjs';

import { RmpcNestedDictionaryType } from '@/api/integrations/rmpc/types/types';

import {
  getRuleErrorMessage,
  checkRule,
  getMinDateFromFieldRule,
  getMaxDateFromFieldRule,
  getOptionsForField,
  isFieldDisabled,
} from './helpers/helper';
import { FormFieldProps, Schema, DateFormField, SelectFormField, TextFormField, SwitchFormField } from './types';
import { RuleKey } from './utils/rules';

export const generateFormFieldsProps = (
  schema: Schema,
  watchedValues: Record<string, string>,
  externalDictionaries?: Record<string, RmpcNestedDictionaryType[]>,
  usedInModal?: boolean,
): FormFieldProps[] => {
  if (!schema || !schema.properties) {
    return [];
  }

  return Object.entries(schema.properties).map(([name, fieldSchema]) => {
    const show = checkRule(RuleKey.SHOW_IF, schema, name, watchedValues);
    const required = checkRule(RuleKey.REQUIRED_IF, schema, name, watchedValues);

    const baseFieldProps = {
      name,
      label: fieldSchema?.label,
      placeholder: fieldSchema?.placeholder,
      show,
      required,
    };

    switch (fieldSchema.uiComponent) {
      case 'date': {
        const { date: minDate, isTodayRule: minDateToday } = getMinDateFromFieldRule(schema, name, watchedValues);
        const { date: maxDate, isTodayRule: maxDateToday } = getMaxDateFromFieldRule(schema, name, watchedValues);

        return {
          ...baseFieldProps,
          component: 'date',
          defaultValue: typeof fieldSchema.default === 'string' ? dayjs(fieldSchema.default) : undefined,
          minDate,
          maxDate,
          maxDateToday,
          minDateToday,
          usedInModal: true,
        } satisfies DateFormField;
      }
      case 'select': {
        const options = getOptionsForField(schema, name, watchedValues, externalDictionaries);
        const disabled = isFieldDisabled(schema, name, watchedValues);

        return {
          ...baseFieldProps,
          component: 'select',
          options,
          defaultValue: undefined,
          useValueForOnChange: true,
          allowClear: true,
          disabled,
        } satisfies SelectFormField;
      }
      case 'text':
        let validationErrorMessage: string | undefined;

        if (!checkRule(RuleKey.GREATER_THAN, schema, name, watchedValues)) {
          validationErrorMessage = getRuleErrorMessage(schema, name, RuleKey.GREATER_THAN);
        }

        const valueType =
          fieldSchema.type === 'number' || fieldSchema.type === 'integer'
            ? (fieldSchema.type as 'number' | 'integer')
            : 'string';

        return {
          ...baseFieldProps,
          component: 'text',
          maxLength: fieldSchema.maxLength,
          defaultValue: typeof fieldSchema.default === 'string' ? fieldSchema.default : undefined,
          type: 'text',
          valueType,
          mask: fieldSchema.mask,
          errorMessage: validationErrorMessage,
          transform: fieldSchema.transform,
        } satisfies TextFormField;
      case 'switch':
        return {
          ...baseFieldProps,
          defaultValue: typeof fieldSchema.default === 'boolean' ? fieldSchema.default : false,
          component: 'switch',
        } satisfies SwitchFormField;
      default:
        return {
          ...baseFieldProps,
          component: 'text',
        } satisfies TextFormField;
    }
  });
};
